'use client';

import { useEffect, useState } from 'react';
import { ErrorState } from '@/components/error/ErrorState';
import {
  captureClientError,
  openUserFeedbackDialog,
  type ErrorCorrelation,
} from '@/lib/observability/client-error-reporting';
import { getStateCopy } from '@/lib/copy/user-facing-messages';

interface RouteErrorFallbackProps {
  error: Error & { digest?: string };
  homeHref?: string;
  homeLabel?: string;
  reset: () => void;
  sectionLabel?: string;
  supportHref?: string;
  viewport?: 'inline' | 'screen';
}

export function RouteErrorFallback({
  error,
  homeHref,
  homeLabel,
  reset,
  sectionLabel,
  supportHref,
  viewport = 'inline',
}: RouteErrorFallbackProps) {
  const pageErrorCopy = getStateCopy('pageError');
  const [correlation, setCorrelation] = useState<ErrorCorrelation | null>(null);

  useEffect(() => {
    console.error(`${sectionLabel || 'Route'} error:`, error);
    setCorrelation(
      captureClientError(error, {
        surface: 'route-error-fallback',
        supportCode: error.digest ?? null,
      })
    );
  }, [error, sectionLabel]);

  return (
    <ErrorState
      error={error}
      eventId={correlation?.eventId ?? undefined}
      homeHref={homeHref}
      homeLabel={homeLabel}
      onRetry={reset}
      onReport={
        correlation?.eventId
          ? () => {
              openUserFeedbackDialog(correlation);
            }
          : undefined
      }
      reportAction={pageErrorCopy.reportAction}
      sectionLabel={sectionLabel}
      supportHref={supportHref}
      viewport={viewport}
    />
  );
}
